import { useEffect, useMemo, useState } from 'react';
import { ListTree } from 'lucide-react';
import {
  apiClient,
  type ActionGroup,
  type ActionType,
  type ActiveFilter,
  type ApiError,
} from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import {
  Field,
  GlassSelect,
  TextInput,
} from '@/components/clients/form-primitives';
import { ConfigModal } from './config-modal';
import { ListToolbar } from './list-toolbar';

interface FormState {
  name: string;
  actionGroupId: string;
  active: boolean;
}

const EMPTY: FormState = {
  name: '',
  actionGroupId: '',
  active: true,
};

export function ActionTypesPage() {
  const [items, setItems] = useState<ActionType[]>([]);
  const [groups, setGroups] = useState<ActionGroup[]>([]);
  const [active, setActive] = useState<ActiveFilter>('true');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ActionType | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .actionGroupsList({ active: 'true' })
      .then((rows) => {
        if (!cancelled) setGroups(rows);
      })
      .catch(() => {
        // sem grupos eh estado valido (vazio)
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiClient
      .actionTypesList({ active, search: search.trim() || undefined })
      .then((rows) => {
        if (!cancelled) setItems(rows);
      })
      .catch((e: ApiError) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [active, search]);

  const groupById = useMemo(() => {
    const m = new Map<string, string>();
    for (const g of groups) m.set(g.id, g.name);
    return m;
  }, [groups]);

  const groupOptions = useMemo(
    () =>
      groups.map((g) => ({
        value: g.id,
        label: g.name,
      })),
    [groups],
  );

  function openNew() {
    setEditing(null);
    setForm({ ...EMPTY, actionGroupId: groups[0]?.id ?? '' });
    setFormError(null);
    setModalOpen(true);
  }

  function openEdit(item: ActionType) {
    setEditing(item);
    setForm({
      name: item.name,
      actionGroupId: item.actionGroupId,
      active: item.active,
    });
    setFormError(null);
    setModalOpen(true);
  }

  async function submit() {
    if (!form.name.trim()) {
      setFormError('Nome obrigatório');
      return;
    }
    if (!form.actionGroupId) {
      setFormError('Selecione um grupo');
      return;
    }
    setSubmitting(true);
    setFormError(null);
    try {
      if (editing) {
        await apiClient.actionTypesUpdate(editing.id, {
          name: form.name.trim(),
          actionGroupId: form.actionGroupId,
          active: form.active,
        });
      } else {
        await apiClient.actionTypesCreate({
          name: form.name.trim(),
          actionGroupId: form.actionGroupId,
          active: form.active,
        });
      }
      setModalOpen(false);
      const rows = await apiClient.actionTypesList({
        active,
        search: search.trim() || undefined,
      });
      setItems(rows);
    } catch (e) {
      setFormError((e as ApiError).message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(item: ActionType) {
    if (!window.confirm(`Excluir o tipo "${item.name}"?`)) return;
    try {
      await apiClient.actionTypesDelete(item.id);
      setItems((prev) => prev.filter((x) => x.id !== item.id));
    } catch (e) {
      window.alert((e as ApiError).message);
    }
  }

  const noGroups = groups.length === 0;

  return (
    <div className="space-y-6">
      <ListToolbar
        title="Tipos de ação"
        description="Tipo específico de processo dentro de cada grupo de ação."
        newLabel="Novo tipo"
        onNew={openNew}
        active={active}
        onActiveChange={setActive}
        search={search}
        onSearchChange={setSearch}
      />

      {noGroups && (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-600">
          Crie pelo menos um <strong>grupo de ação</strong> antes de cadastrar
          tipos.
        </div>
      )}

      <div className="overflow-hidden rounded-lg border border-[var(--border)] bg-[var(--surface)]">
        {loading ? (
          <p className="px-4 py-8 text-center text-sm text-[var(--fg-muted)]">
            Carregando…
          </p>
        ) : error ? (
          <p className="px-4 py-8 text-center text-sm text-red-500">{error}</p>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-3 px-4 py-12 text-center">
            <ListTree className="h-10 w-10 text-[var(--fg-muted)] opacity-40" />
            <p className="text-sm text-[var(--fg-muted)]">
              Nenhum tipo cadastrado.
            </p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[color-mix(in_oklch,var(--fg)_4%,transparent)] text-left text-xs uppercase tracking-wide text-[var(--fg-muted)]">
              <tr>
                <th className="px-4 py-2 font-medium">Nome</th>
                <th className="px-4 py-2 font-medium">Grupo</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr
                  key={item.id}
                  className="border-t border-[var(--border)] transition hover:bg-[color-mix(in_oklch,var(--fg)_4%,transparent)]"
                >
                  <td className="px-4 py-2">
                    <button
                      type="button"
                      onClick={() => openEdit(item)}
                      className="text-left font-medium text-[var(--fg)] hover:text-[var(--accent)]"
                    >
                      {item.name}
                    </button>
                  </td>
                  <td className="px-4 py-2 text-[var(--fg-muted)]">
                    {groupById.get(item.actionGroupId) ?? '—'}
                  </td>
                  <td className="px-4 py-2">
                    <span
                      className={
                        item.active
                          ? 'inline-flex items-center rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-medium text-emerald-500'
                          : 'inline-flex items-center rounded-full bg-zinc-500/15 px-2 py-0.5 text-xs font-medium text-zinc-400'
                      }
                    >
                      {item.active ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(item)}
                    >
                      Excluir
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <ConfigModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Editar tipo de ação' : 'Novo tipo de ação'}
        description="Tipo específico de processo (ex: Ação de Alimentos)."
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => setModalOpen(false)}
              disabled={submitting}
            >
              Cancelar
            </Button>
            <Button
              onClick={submit}
              disabled={submitting || noGroups}
            >
              {submitting ? 'Salvando…' : 'Salvar'}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {formError && (
            <p className="rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-500">
              {formError}
            </p>
          )}
          <Field label="Nome" required>
            <TextInput
              value={form.name}
              onChange={(e) =>
                setForm((s) => ({ ...s, name: e.target.value }))
              }
              placeholder="Ex: Ação de Alimentos"
              maxLength={160}
            />
          </Field>
          <Field label="Grupo de ação" required>
            <GlassSelect
              value={form.actionGroupId}
              onChange={(v) => setForm((s) => ({ ...s, actionGroupId: v }))}
              options={groupOptions}
              placeholder={noGroups ? 'Cadastre um grupo antes' : 'Selecione'}
              disabled={noGroups}
            />
          </Field>
          <label className="flex items-center gap-2 text-sm text-[var(--fg)]">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) =>
                setForm((s) => ({ ...s, active: e.target.checked }))
              }
              className="h-4 w-4 rounded border-[var(--border)]"
            />
            Ativo
          </label>
        </div>
      </ConfigModal>
    </div>
  );
}
